import { useState, useMemo, useEffect } from "react";
import { parseISO, differenceInDays, format, parse } from "date-fns";
import { useTranslation } from "react-i18next";

import { useViewNDVIContext } from "../ViewNDVIContext";
import { Loading } from "../../../../../../../_metronic/elements/Loading";
import { formatFixedNumber } from "../../../../../../shared/numberFormat";
import AvailableDatePicker from "./AvailableDatePicker";

const formatRankingVariation = (value) => {
  const numericValue = Number(value || 0);
  return numericValue > 0 ? `+${numericValue}` : String(numericValue);
};

const DateSelector = ({ text, date, setDate, openedDatePicker, setOpenedDatePicker, availableDates }) => {
  return (
    <AvailableDatePicker
      availableDates={availableDates}
      date={date}
      label={text}
      openedDatePicker={openedDatePicker}
      setDate={setDate}
      setOpenedDatePicker={setOpenedDatePicker}
    />
  )
};

const getClosestDate = (dates, targetDate) => {
  let currentDiff = Number.MAX_VALUE;
  let choiced = null;

  dates.forEach(report => {
    const diff = Math.abs(differenceInDays(targetDate, parseISO(report.date)));
    if (diff < currentDiff) {
      currentDiff = diff;
      choiced = report;
    }
  });

  return choiced
};

const getRanking = (report, firstDate, secondDate) => {
  const { lands, ndvi_land_dates } = report;
  const reportDatesGroupByLand = {};

  ndvi_land_dates.forEach(date => {
    if (!reportDatesGroupByLand[date.land_id]) {
      reportDatesGroupByLand[date.land_id] = [];
    }

    reportDatesGroupByLand[date.land_id].push(date);
  });

  const firstDatesReport = Object.values(reportDatesGroupByLand).map(
    e => getClosestDate(e.filter(e => e.usage == "USED"), firstDate)
  ).filter(Boolean).sort(
    (a, b) => a.avg_index > b.avg_index ? -1 : 1
  );
  const secondDatesReport = Object.values(reportDatesGroupByLand).map(
    e => getClosestDate(e.filter(e => e.usage == "USED"), secondDate)
  ).filter(Boolean).sort(
    (a, b) => a.avg_index > b.avg_index ? -1 : 1
  );

  const total = [];
  for (var z in firstDatesReport) {
    const currentLand = lands.filter(e => e.land_id == firstDatesReport[z].land_id)[0];

    if (!currentLand) {
      continue;
    }

    const secondMatch = secondDatesReport.map(
      (q, index) => [q.land_id == currentLand.land_id, index, q]
    ).filter(
      e => e[0]
    )[0];

    if (!secondMatch) {
      continue;
    }

    const secondIndex = secondMatch[1];
    const secondValue = secondMatch[2];
    const initialPosition = parseInt(z) + 1;
    const finalPosition = parseInt(secondIndex) + 1;

    total.push({
      land: currentLand,
      initialPosition,
      position: initialPosition,
      firstAvgIndex: firstDatesReport[z].avg_index,
      finalPosition,
      secondRankingPosition: finalPosition,
      secondIndex: secondValue.avg_index,
      variation: initialPosition - finalPosition
    });
  }

  return total;
}


const Ranking = () => {
  const context = useViewNDVIContext();
  const [firstDate, setFirstDate] = useState(null);
  const [secondDate, setSecondDate] = useState(null);
  const [firstOpenedDatePicker, setFirstOpenedDatePicker] = useState(false);
  const [secondOpenedDatePicker, setSecondOpenedDatePicker] = useState(false);
  const { t } = useTranslation();
  const { ranking, availableDates } = useMemo(() => {
    if (firstDate == null || secondDate == null) {
      return { ranking: null, availableDates: null };
    }

    const availableDates = context.report.ndvi_land_dates.filter(e => e.usage == "USED").map(e => parseISO(e.date)).map(e => format(e, "yyyy-LL-dd"))
    return { ranking: getRanking(context.report, firstDate, secondDate), availableDates };
  }, [firstDate, secondDate, context.report]);

  useEffect(() => {
    const dates = context.report.ndvi_land_dates.filter(e => e.usage == "USED").map(e =>
      [parseISO(e.date), parse(e.date, "yyyy-LL-dd", new Date())]
    ).sort((a, b) => a[1] - b[1]);
    if (dates.length < 1) {
      return;
    }
    setFirstDate(dates[0][0]);
    setSecondDate(dates[dates.length - 1][0]);
  }, [])

  if (ranking == null) {
    return <><Loading /></>;
  }

  return (
    <>
      <div className="row">
        <div className="col-6">
          <DateSelector
            text={t("reports:ndvi.view.reports.ranking.initial_date")}
            date={firstDate}
            setDate={setFirstDate}
            openedDatePicker={firstOpenedDatePicker}
            setOpenedDatePicker={setFirstOpenedDatePicker}
            availableDates={availableDates} />
        </div>
        <div className="col-6">
          <DateSelector
            text={t("reports:ndvi.view.reports.ranking.final_date")}
            date={secondDate}
            setDate={setSecondDate}
            openedDatePicker={secondOpenedDatePicker}
            setOpenedDatePicker={setSecondOpenedDatePicker}
            availableDates={availableDates}
            minDate={secondDate} />
        </div>
      </div>
      <div className="table-responsive">
        <table className="table table-row-bordered">
          <thead>
            <tr className="fw-bold fs-7 text-gray-800">
              <th>{t("reports:ndvi.view.reports.ranking.columns.land")}</th>
              <th>{t("reports:ndvi.view.reports.ranking.columns.initial_position")}</th>
              <th>{t("reports:ndvi.view.reports.ranking.columns.ndvi_initial_date")}</th>
              <th>{t("reports:ndvi.view.reports.ranking.columns.final_position")}</th>
              <th>{t("reports:ndvi.view.reports.ranking.columns.ndvi_final_date")}</th>
              <th>{t("reports:ndvi.view.reports.ranking.columns.variation_ranking")}</th>
            </tr>
          </thead>
          <tbody>
            {ranking.map(e => {
              const variation = e.variation;
              let color = "#ffffff";
              if (variation > 0) {
                color = "#7cff9b"
              } else if (variation < 0) {
                color = "#ff7c7c"
              }
              return (
                <tr key={e.land.land_id}>
                  <td>{e.land.name}</td>
                  <td>{e.initialPosition}</td>
                  <td>{formatFixedNumber(e.firstAvgIndex, 3)}</td>
                  <td>{e.finalPosition}</td>
                  <td>{formatFixedNumber(e.secondIndex, 3)}</td>
                  <td style={{ backgroundColor: color }}>{formatRankingVariation(variation)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default Ranking;
